import styled from '@emotion/styled'
import {
    PRIMARY_PAGE_TEXT_COLOR_VAR,
    PRIMARY_PAGE_FILL_COLOR_VAR,
    SECONDARY_FONT_CSS_VAR,
} from '../../../context/CustomizationContextProvider'
import {
    inScrollingLayout,
    pageBleedTrackStyles,
    scaledFontSize,
    ScrollingLayout,
    sectionGutter,
    sectionMedia,
    SectionMedia,
} from '../../../style/scrollingLayout'
import { Constants } from '../../../style/styleConstants'
import { getTypographyRoleStyle } from '../../../style/typography'
import { TYPOGRAPHY_SIZE } from '../../../style/typographySizes'

export const SectionContainer = styled.div<{
    verticalPadding?: number
    paddingTop?: number
    paddingBottom?: number
    /**
     * Holds content to a readable width once the section is wider than that,
     * centred in the section so it lines up with whatever sits above and
     * below. Text inside stays left-aligned. Opt-in, because most sections
     * want the full width.
     */
    maxContentWidth?: number
}>(({ verticalPadding, paddingTop, paddingBottom, maxContentWidth }) => ({
    // Horizontal padding is applied by PaddedWrapper in RenderSections.
    marginTop: 0,
    paddingTop:
        paddingTop ?? verticalPadding ?? Constants.SECTION_BASE_SPACING_PX,
    paddingBottom:
        paddingBottom ?? verticalPadding ?? Constants.SECTION_BASE_SPACING_PX,
    width: '100%',
    maxWidth: '100vw',
    minWidth: 0,
    boxSizing: 'border-box',
    ...(maxContentWidth === undefined
        ? null
        : {
              [sectionMedia(maxContentWidth)]: {
                  maxWidth: maxContentWidth,
                  marginInline: 'auto',
              },
          }),
}))

export const SectionTitle = styled.h2<{ isHorizontalScroll?: boolean }>(
    ({ isHorizontalScroll }) => ({
        // H2 — Section Heading
        ...getTypographyRoleStyle('headingText', {
            fontFamily: `var(${SECONDARY_FONT_CSS_VAR})`,
            fontWeight: 600,
            lineHeight: 1.35,
        }),
        fontSize: scaledFontSize(TYPOGRAPHY_SIZE.SECTION),
        marginTop: 0,
        marginBottom: Constants.SECTION_BASE_SPACING_PX,
        color: `var(${PRIMARY_PAGE_TEXT_COLOR_VAR})`,
        ...inScrollingLayout({ textAlign: 'center' }),
        ...(isHorizontalScroll && {
            paddingLeft: `${Constants.CONTENT_BLOCK_MOBILE_MARGIN}px`,
            paddingRight: `${Constants.CONTENT_BLOCK_MOBILE_MARGIN}px`,
            ...inScrollingLayout({
                textAlign: 'center',
                paddingInline: sectionGutter,
            }),
        }),
    }),
)

const GRID_COLUMNS_MD = 3
const GRID_COLUMN_GAP_PX = 12

export const MAX_GRID_COLUMNS = 4

/** Widest grid card; used for image `sizes` since CSS can't read column count. */
export const MAX_GRID_CARD_WIDTH_PX = Math.round(
    (ScrollingLayout.CONTENT_MAX_WIDTH -
        2 * ScrollingLayout.PAGE_GUTTER_PX -
        (MAX_GRID_COLUMNS - 1) * GRID_COLUMN_GAP_PX) /
        MAX_GRID_COLUMNS,
)

export const responsiveGridColumns = {
    [SectionMedia.MD]: {
        gridTemplateColumns: `repeat(${GRID_COLUMNS_MD}, minmax(0, 1fr))`,
    },
    [SectionMedia.LG]: {
        gridTemplateColumns: `repeat(${MAX_GRID_COLUMNS}, minmax(0, 1fr))`,
    },
}

export const ProductGridContainer = styled.div<{ columnCount: number }>(
    (props) => ({
        display: 'grid',
        gridTemplateColumns: `repeat(${props.columnCount}, minmax(0, 1fr))`,
        ...responsiveGridColumns,
        gridAutoRows: 'auto',
        width: '100%',
        position: 'relative',
        background: `var(${PRIMARY_PAGE_FILL_COLOR_VAR})`,
        paddingTop: 8,
        paddingBottom: 16,
        minHeight: 'calc(100% - 95px)',
        gap: `16px ${GRID_COLUMN_GAP_PX}px`,
        boxSizing: 'border-box',
    }),
)

// Fixed card width for horizontal product grids. Source of truth — the
// `sizes` attribute used by product images in horizontal scroll is derived
// from this constant in PLPDrawer.tsx.
export const HORIZONTAL_SCROLL_CARD_WIDTH_PX = 185

// The subgrid rows are: image, text, button. Each card spans all three so
// image / text / button rows align across sibling cards regardless of image
// aspect ratio or title line count.
export const ProductScrollItem = styled.div({
    gridRow: '1 / -1',
    display: 'grid',
    gridTemplateRows: 'subgrid',
    rowGap: 8,
    width: `${HORIZONTAL_SCROLL_CARD_WIDTH_PX}px`,
    minWidth: 0,
    '& > *': {
        gridRow: '1 / -1',
    },
})

export const ProductScrollContainer = styled.div<{ noPadding?: boolean }>(
    ({ noPadding }) => ({
        display: 'grid',
        gridAutoFlow: 'column',
        gridAutoColumns: `${HORIZONTAL_SCROLL_CARD_WIDTH_PX}px`,
        gridTemplateRows: 'auto 1fr auto',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none',
        paddingTop: 0,
        paddingBottom: 12,
        gap: '12px',
        paddingLeft: noPadding
            ? 0
            : `${Constants.CONTENT_BLOCK_MOBILE_MARGIN}px`,
        paddingRight: noPadding
            ? 0
            : `${Constants.CONTENT_BLOCK_MOBILE_MARGIN}px`,
        '&::-webkit-scrollbar': {
            display: 'none',
        },
        // Runs to the page edges so cards scroll out of the content column.
        // Too few cards to fill the track should sit in the middle of it;
        // `safe` keeps the first card reachable when there are enough of them
        // to overflow. Combined with the bleed styles under one
        // `inScrollingLayout` so the container-query key isn't overwritten.
        ...(noPadding
            ? null
            : inScrollingLayout({
                  ...pageBleedTrackStyles,
                  justifyContent: 'safe center',
              })),
    }),
)
